import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

/**
 * Nagłówek strony (poziomu widoku): tintowany kwadrat ikony + tytuł + podtytuł +
 * slot akcji po prawej. Sklonowany ze wzorca `PageHeader` z ITE Workflow365.
 *
 * Uwaga: to inny byt niż `layout-elements/PageHeaderElement` (element Kreatora
 * osadzany w formularzu modułu) — stąd odrębna nazwa `PageHeaderBar`.
 */
type IconColor =
  | 'red'
  | 'orange'
  | 'amber'
  | 'yellow'
  | 'lime'
  | 'green'
  | 'emerald'
  | 'teal'
  | 'cyan'
  | 'sky'
  | 'blue'
  | 'indigo'
  | 'violet'
  | 'purple'
  | 'pink'
  | 'gray'
  | 'accent';

interface PageHeaderBarProps {
  /**
   * Opcjonalna ikona nagłówka. Wg Figmy (Section header) nagłówek strony to tytuł + opis
   * BEZ kolorowego kafelka — dlatego domyślnie ikony NIE renderujemy. Podaj `icon`, tylko
   * gdy dany ekran świadomie chce kafelek.
   */
  icon?: LucideIcon;
  title: string;
  description?: string;
  iconColor?: IconColor;
  actions?: ReactNode;
}

const COLOR_CLASSES: Record<IconColor, { bg: string; text: string }> = {
  red: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400' },
  orange: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-600 dark:text-orange-400',
  },
  amber: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-600 dark:text-amber-400' },
  yellow: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    text: 'text-yellow-600 dark:text-yellow-400',
  },
  lime: { bg: 'bg-lime-100 dark:bg-lime-900/30', text: 'text-lime-600 dark:text-lime-400' },
  green: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-600 dark:text-green-400' },
  emerald: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/30',
    text: 'text-emerald-600 dark:text-emerald-400',
  },
  teal: { bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-600 dark:text-teal-400' },
  cyan: { bg: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-600 dark:text-cyan-400' },
  sky: { bg: 'bg-sky-100 dark:bg-sky-900/30', text: 'text-sky-600 dark:text-sky-400' },
  blue: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400' },
  indigo: {
    bg: 'bg-indigo-100 dark:bg-indigo-900/30',
    text: 'text-indigo-600 dark:text-indigo-400',
  },
  violet: {
    bg: 'bg-violet-100 dark:bg-violet-900/30',
    text: 'text-violet-600 dark:text-violet-400',
  },
  purple: {
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    text: 'text-purple-600 dark:text-purple-400',
  },
  pink: { bg: 'bg-pink-100 dark:bg-pink-900/30', text: 'text-pink-600 dark:text-pink-400' },
  gray: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-600 dark:text-gray-400' },
  accent: {
    bg: 'bg-accent-100 dark:bg-accent-900/30',
    text: 'text-accent-600 dark:text-accent-400',
  },
};

export function PageHeaderBar({
  icon: Icon,
  title,
  description,
  iconColor = 'accent',
  actions,
}: PageHeaderBarProps) {
  const colors = COLOR_CLASSES[iconColor];
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-3">
        {Icon && (
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${colors.bg}`}
          >
            <Icon className={`h-5 w-5 ${colors.text}`} />
          </div>
        )}
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-bold text-text-primary">{title}</h1>
          {description && <p className="text-sm text-text-tertiary">{description}</p>}
        </div>
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

export default PageHeaderBar;
